import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  VERIFY_SUCCESS: 'VERIFY_SUCCESS',
  VERIFY_FAILURE: 'VERIFY_FAILURE',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    case AUTH_ACTIONS.VERIFY_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    case AUTH_ACTIONS.VERIFY_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');

      // Check for potential data format migration needed
      let needsDataMigration = false;
      if (userData) {
        try {
          const parsedData = JSON.parse(userData);
          // Check if old format without isSystemAdmin or user_type fields
          if (!parsedData.hasOwnProperty('isSystemAdmin') && !parsedData.hasOwnProperty('user_type')) {
            needsDataMigration = true;
          }
        } catch (error) {
          needsDataMigration = true;
        }
      }

      // Clear localStorage if migration needed or if data is corrupted
      if (needsDataMigration) {
        console.log('Clearing old user data format for migration');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        dispatch({ type: AUTH_ACTIONS.VERIFY_FAILURE });
        return;
      }

      if (token && userData) {
        try {
          // Verify token with server
          const response = await authAPI.verify();

          dispatch({
            type: AUTH_ACTIONS.VERIFY_SUCCESS,
            payload: response.data.data
          });
        } catch (error) {
          console.error('Token verification failed:', error);
          // Clear invalid token
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          dispatch({ type: AUTH_ACTIONS.VERIFY_FAILURE });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.VERIFY_FAILURE });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await authAPI.login(credentials);
      const { token, user } = response.data.data;

      // Store in localStorage
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_data', JSON.stringify(user));

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { token, user }
      });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: message
      });
      return { success: false, error: message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');

      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      return { success: false, error: message };
    }
  };

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // Check if user has access to location
  const hasLocationAccess = (locationId) => {
    if (!state.user || !state.user.locations) return false;
    return state.user.locations.some(loc => loc.id === locationId);
  };

  // Check if user is owner of location
  const isLocationOwner = (locationId) => {
    if (!state.user || !state.user.locations) return false;
    const location = state.user.locations.find(loc => loc.id === locationId);
    return location?.role === 'owner';
  };

  // Get user's locations
  const getUserLocations = () => {
    return state.user?.locations || [];
  };

  // Get user's location IDs
  const getUserLocationIds = () => {
    return state.user?.locations?.map(loc => loc.id) || [];
  };

  // Check if user is system admin
  const isSystemAdmin = () => {
    return state.user?.isSystemAdmin || state.user?.user_type === 'system_admin';
  };

  const value = {
    // State
    ...state,

    // Actions
    login,
    logout,
    register,
    clearError,

    // Helper functions
    hasLocationAccess,
    isLocationOwner,
    getUserLocations,
    getUserLocationIds,
    isSystemAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;